import { Fragment } from "react";
import { Badge, Button } from "@conuti-das/prince-ui";
import type { BridgeRow as Row, Side } from "./bridgeModel";
import { TransformExplainer } from "./TransformExplainer";

const TF_COLOR: Record<string, string> = {
  identity: "var(--sapNeutralColor, #6a6d70)",
  code_lookup: "var(--sapInformativeColor, #0071e3)",
  date_format: "var(--sapPositiveColor, #34c759)",
};

/** Maps mapping status to a badge tone */
function statusTone(status: string): "green" | "orange" | "neutral" {
  if (status === "approved") return "green";
  if (status === "needs_review") return "orange";
  return "neutral";
}

function FieldCard({ field, align }: { field: { segment?: string; detail?: string; name: string; messageType?: string }; align: "left" | "right" }) {
  return (
    <div style={{ background: "var(--sapField_Background)", border: "1px solid var(--sapField_BorderColor)", borderRadius: "8px", padding: "7px 10px", textAlign: align }}>
      <div style={{ fontFamily: "monospace", fontSize: "0.7rem", color: "var(--sapContent_LabelColor)" }}>
        {field.messageType && <Badge tone="teal" style={{ marginRight: "4px" }}>{field.messageType}</Badge>}
        {field.segment ? `${field.segment}${field.detail ? " · " + field.detail : ""}` : field.detail}
      </div>
      <div style={{ fontWeight: 600, fontSize: "0.86rem" }}>{field.name}</div>
    </div>
  );
}

export function BridgeRow({
  row, leftSide, expanded, onToggle, onAddMapping, onOpenEntry, onAddBoSource,
}: {
  row: Row;
  leftSide: Side; // which logical side is the left column
  expanded: boolean;
  onToggle: () => void;
  onAddMapping: (row: Row) => void;
  onOpenEntry: (mappingId: string) => void;
  onAddBoSource: (row: Row) => void;
}) {
  // spine is EDI for msgtype rows (row.ediAnchor set) else BO field.
  const spineIsEdi = row.ediAnchor !== undefined;
  const spineOnLeft = (leftSide === "edi") === spineIsEdi;

  const spineCard = <FieldCard field={row.spine} align={spineOnLeft ? "left" : "right"} />;

  const matchesCol =
    row.matches.length === 0 ? (
      row.mappable ? (
        row.ediAnchor ? (
          <Button variant="plain" onPress={() => onAddMapping(row)} data-testid={`add-${row.key}`}>+ mappen</Button>
        ) : (
          <Button variant="plain" onPress={() => onAddBoSource(row)} data-testid={`addbo-${row.key}`}>+ EDI-Quelle</Button>
        )
      ) : (
        <span style={{ color: "var(--sapContent_LabelColor)", opacity: 0.5 }}>kein BO4E</span>
      )
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {row.matches.map((m) => (
          <div key={m.mappingId} role="button" onClick={() => onOpenEntry(m.mappingId)} style={{ cursor: "pointer" }}>
            <FieldCard field={m.target} align={spineOnLeft ? "right" : "left"} />
            {m.status && (
              <span
                data-testid={`status-${m.mappingId}`}
                style={{ display: "block", textAlign: spineOnLeft ? "right" : "left" }}
              >
                <Badge
                  tone={statusTone(m.status)}
                  style={{ fontSize: "0.7rem" }}
                >
                  {m.status}
                </Badge>
              </span>
            )}
          </div>
        ))}
      </div>
    );

  const transform = row.matches[0]?.transform;
  const pillMappingId = row.matches[0]?.mappingId;
  const connector = (
    <div style={{ position: "relative", height: "2px", background: row.matches.length ? "var(--sapList_BorderColor)" : "transparent", alignSelf: "center" }}>
      {transform && (
        <span style={{ position: "absolute", top: "-11px", left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
          <span
            data-testid={`pill-${row.key}`}
            role="button"
            onClick={() => { if (pillMappingId) onOpenEntry(pillMappingId); }}
            style={{ cursor: "pointer", padding: "2px 8px", borderRadius: "999px", fontSize: "0.68rem", fontWeight: 700, color: "#fff", background: TF_COLOR[transform.type] ?? "var(--sapNeutralColor,#6a6d70)" }}
          >
            {transform.type}
          </span>
          <span
            data-testid={`explain-${row.key}`}
            role="button"
            aria-label="Transformation erklären"
            onClick={onToggle}
            style={{ cursor: "pointer", fontSize: "0.72rem", lineHeight: 1, color: "var(--sapContent_LabelColor)" }}
          >
            ⓘ
          </span>
        </span>
      )}
    </div>
  );

  const left = spineOnLeft ? spineCard : matchesCol;
  const right = spineOnLeft ? matchesCol : spineCard;

  return (
    <Fragment>
      <div style={{ alignSelf: "center" }} data-testid={`row-${row.key}`}>{left}</div>
      {connector}
      <div style={{ alignSelf: "center" }}>{right}</div>
      {expanded && transform && (
        <div style={{ gridColumn: "1 / 4", margin: "4px 0 10px" }}>
          <TransformExplainer transform={transform} />
        </div>
      )}
    </Fragment>
  );
}

